import { useState } from 'react'
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@cutilei/ui'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { getApiErrorMessage, logApiError } from 'services/apiError'
import General from 'services/General'
import MaskedInputService from 'services/maskedInput'
import Select from 'components/Inputs/Select'
import MainButton from 'components/Buttons/MainButton'
import submitOnEnter from 'utils/submitOnEnter'
import customerValidator from 'validators/customerValidator'
import { DateTime } from 'luxon'
import { Formik } from 'formik'

import {
  Form,
  FormField,
  Input,
  MaskedInput,
  Row,
  InfoText,
  WarningText,
  ErrorContainer
} from './styles'

export default function AddCustomerModal ({
  visible,
  query = '',
  onConfirm: handleConfirm,
  onClose: handleClose
}) {
  const [errorMessage, setErrorMessage] = useState (null)
  const businessId = AuthService.getBusinessId ()
  const token = AuthService.getToken ()
  const queryType = General.getStringType (query)

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  return (
    <Dialog open={visible} onOpenChange={open => { if (!open) handleClose() }}>
      <DialogContent size='lg' closeOnOutsideClick={false} aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Cadastrar cliente</DialogTitle>
        </DialogHeader>
        <Formik
          validationSchema={customerValidator}
          validateOnChange={false}
          validateOnBlur={true}
          initialValues={{
            'id': '',
            'name': queryType === 'text' ? query : '',
            'email': queryType === 'email' ? query : '',
            'phone': queryType === 'phone' ? query : '',
            'gender': null,
            'birth': ''
          }}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              const { data: customer } = await cutileiApi.post (
                `/businesses/${businessId}/customers`, {
                  ...values,
                  gender: values.gender?.value ?? null,
                  birth: values.birth !== ''
                    ? DateTime.fromFormat (values.birth, 'ddMMyyyy').toISODate ()
                    : null
                }, requestConfig
              )
            
              setSubmitting (false)
              handleConfirm ({ balance: 0, ...customer })
              handleClose ()
            } catch (error) {
              setSubmitting (false)
              setErrorMessage (getApiErrorMessage (error))
              logApiError (error)
            }
          }}
        >
          {({
            values,
            errors,
            touched,
            isSubmitting,
            setFieldValue,
            setFieldTouched,
            handleChange,
            handleBlur,
            handleSubmit
          }) => {
            return (
              <>
                <DialogBody>
                  <Form onKeyDown={submitOnEnter(handleSubmit, isSubmitting)}>
                    <Row>
                      <FormField>
                        <Input 
                          placeholder='Nome'
                          value={values.name}
                          onChange={handleChange ('name')} 
                          onBlur={handleBlur ('name')}
                        />
                        {touched.name && errors.name && (
                          <WarningText>
                            {errors.name}
                          </WarningText>
                        )}
                      </FormField>
                      <FormField>
                        <Input
                          type='email'
                          placeholder='E-mail'
                          value={values.email}
                          onChange={handleChange ('email')}
                          onBlur={handleBlur ('email')}
                        />
                        {touched.email && errors.email && (
                          <WarningText>
                            {errors.email}
                          </WarningText>
                        )}
                      </FormField>
                    </Row>
                    <Row>
                      <FormField>
                        <MaskedInput
                          name='phone'
                          placeholder='Celular'
                          options={{
                            blocks: [0, 2, 5, 4],
                            delimiters: ['(', ') ', '-'],
                            numericOnly: true
                          }}
                          value={values.phone}
                          onChange={event => MaskedInputService.handleChange (event, setFieldValue)}
                          onBlur={handleBlur ('phone')}
                        />
                        {touched.phone && errors.phone && (
                          <WarningText>
                            {errors.phone}
                          </WarningText>
                        )}
                      </FormField>
                      <FormField>
                        <Select
                          name='gender'
                          placeholder='Gênero...'
                          value={values.gender}
                          options={[
                            {label: 'Masculino', value: 'Masculino'},
                            {label: 'Feminino', value: 'Feminino'},
                            {label: 'Não informar', value: 'Não informar'}
                          ]}
                          onChange={setFieldValue}
                          onBlur={setFieldTouched}
                          error={errors.gender}
                          touched={touched.gender}
                        />
                        {touched.gender && errors.gender && (
                          <WarningText>{errors.gender}</WarningText>
                        )}
                      </FormField>
                      <FormField>
                        <MaskedInput
                          name='birth'
                          placeholder='Nascimento'
                          options={{date: true, datePattern: ['d', 'm', 'Y']}}
                          value={values.birth}
                          onChange={event => MaskedInputService.handleChange (event, setFieldValue)}
                          onBlur={handleBlur ('birth')}
                        />
                        {touched.birth && errors.birth && (
                          <WarningText>{errors.birth}</WarningText>
                        )}
                      </FormField>
                    </Row>
                    {errorMessage && (
                      <ErrorContainer>
                        <InfoText>{errorMessage}</InfoText>
                      </ErrorContainer>
                    )}
                  </Form>
                </DialogBody>
                <DialogFooter>
                  <MainButton
                    label='Cadastrar'
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    loading={isSubmitting}
                    width={180}
                  />
                </DialogFooter>
              </>
            )
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  )
}
